/**
 * SecureShield Pro storefront — presentation only.
 * Price/currency/purchase mode come from GET /v1/catalog (consumer-platform).
 * Download is never a permanent public artifact URL; entitled customers obtain a
 * short-lived URL via the account API. Signing/notarization claims require
 * verified release evidence.
 */

/** Pro Developer ID bundle id — never the App Store edition id. */
export const SECUREMAC_PRO_IDENTIFIER = "ai.lightningflow.securemac";
export const SECUREMAC_PRO_CATALOG_KEY = "securemac-pro";
const SECUREMAC_PRO_APPROVED_CURRENCY = "usd";
const SECUREMAC_PRO_APPROVED_PURCHASE_MODE = "one_time";
const SECUREMAC_PRO_APPROVED_UNIT_AMOUNT = 1999;

export function formatUsdFromCents(unitAmount) {
  if (!Number.isInteger(unitAmount) || unitAmount <= 0) return "";
  const dollars = (unitAmount / 100).toFixed(2);
  return `US$${dollars}`;
}

function baseStorefront() {
  return {
    product_name: "SecureShield Pro",
    identifier: SECUREMAC_PRO_IDENTIFIER,
    catalog_key: SECUREMAC_PRO_CATALOG_KEY,
    pricing_published: false,
    price_usd: "",
    currency: "",
    purchase_mode: "",
    verification_status: "not_ready",
    checkout_enabled: false,
    license_model: "One-time purchase. No subscription.",
    launch: {
      enabled: false,
    },
    download: {
      // Never a permanent artifact URL. Account issues short-lived tickets.
      live: false,
      url: "",
      route: "/account",
    },
    requirements: {
      os: "macOS 14.4 or later",
      // v1 claims Apple Silicon only unless a clean Intel runtime receipt exists
      // on the verified release manifest.
      chip: "Apple Silicon",
      distribution: "Direct download through your LightningFlow account",
    },
    checks_count: 14,
    release_evidence: {
      present: false,
      signed_and_notarized: false,
      version: "",
      build: "",
      architectures: [],
      minimum_macos: "",
    },
    network_guard: {
      required_for_launch: true,
      included_in_launch: false,
    },
    claims: {
      available_now: false,
      signed_and_notarized: false,
    },
    launch_summary:
      "Check your LightningFlow account for availability.",
  };
}

/**
 * Merge a /v1/catalog product view into the Pro storefront.
 * Checkout stays disabled unless verification_status === "ready".
 */
export function applyCatalogProduct(storefront, product) {
  const next = {
    ...storefront,
    launch: { ...storefront.launch },
    download: { ...storefront.download },
    requirements: { ...storefront.requirements },
    release_evidence: { ...storefront.release_evidence },
    network_guard: { ...storefront.network_guard },
    claims: { ...storefront.claims },
  };

  if (!product || product.key !== SECUREMAC_PRO_CATALOG_KEY) {
    next.pricing_published = false;
    next.price_usd = "";
    next.currency = "";
    next.purchase_mode = "";
    next.verification_status = "not_ready";
    next.checkout_enabled = false;
    next.launch.enabled = false;
    return next;
  }

  const ready = product.verification_status === "ready"
    && product.enabled !== false
    && String(product.currency || "").toLowerCase() === SECUREMAC_PRO_APPROVED_CURRENCY
    && product.purchase_mode === SECUREMAC_PRO_APPROVED_PURCHASE_MODE
    && product.unit_amount === SECUREMAC_PRO_APPROVED_UNIT_AMOUNT;
  const priceUsd = ready ? formatUsdFromCents(product.unit_amount) : "";
  next.verification_status = product.verification_status || "not_ready";
  next.currency = ready ? String(product.currency || "").toLowerCase() : "";
  next.purchase_mode = ready ? String(product.purchase_mode || "") : "";
  next.pricing_published = Boolean(ready && priceUsd);
  next.price_usd = priceUsd;
  next.checkout_enabled = ready && next.pricing_published;
  next.launch.enabled = next.checkout_enabled;
  next.launch_summary = next.checkout_enabled
    ? "Buy once. Download from your LightningFlow account."
    : "Check your LightningFlow account for availability.";
  return next;
}

/**
 * Merge verified release evidence. Without it, pages must not claim
 * signed/notarized, available now, or a permanent download artifact.
 */
export function applyReleaseEvidence(storefront, evidence) {
  const next = {
    ...storefront,
    launch: { ...storefront.launch },
    download: { ...storefront.download },
    requirements: { ...storefront.requirements },
    release_evidence: { ...storefront.release_evidence },
    network_guard: { ...storefront.network_guard },
    claims: { ...storefront.claims },
  };

  const present = Boolean(
    evidence
    && evidence.release_id
    && evidence.artifact_sha256
    && Array.isArray(evidence.architectures)
    && evidence.architectures.length > 0
  );

  next.release_evidence = {
    present,
    signed_and_notarized: present && evidence.signed_and_notarized === true,
    version: present ? String(evidence.version || "") : "",
    build: present ? String(evidence.build || "") : "",
    architectures: present ? [...evidence.architectures] : [],
    minimum_macos: present ? String(evidence.minimum_macos || "") : "",
  };

  if (present && evidence.minimum_macos) {
    next.requirements.os = evidence.minimum_macos.startsWith("macOS")
      ? evidence.minimum_macos
      : `macOS ${evidence.minimum_macos} or later`;
  }

  const arches = next.release_evidence.architectures.map((a) => String(a).toLowerCase());
  const appleSilicon = arches.some((a) => a === "arm64" || a === "aarch64");
  const intel = arches.some((a) => a === "x86_64" || a === "x86-64" || a === "intel");
  if (present && appleSilicon && intel && evidence.intel_runtime_verified === true) {
    next.requirements.chip = "Apple Silicon or Intel";
  } else {
    next.requirements.chip = "Apple Silicon";
  }

  next.claims.signed_and_notarized = next.release_evidence.signed_and_notarized;
  next.claims.available_now = present && storefront.checkout_enabled;
  next.network_guard.included_in_launch = present && evidence.includes_network_guard === true;

  if (next.claims.signed_and_notarized) {
    next.requirements.distribution =
      "Direct download, Developer ID signed and notarized by Apple";
  } else {
    next.requirements.distribution = "Direct download through your LightningFlow account";
  }

  // Entitled downloads always go through /account — never a permanent artifact URL.
  next.download = {
    live: present && storefront.checkout_enabled,
    url: "",
    route: "/account",
  };

  return next;
}

export function buildSecureMacProStorefront(env = process.env, catalogProduct = null, releaseEvidence = null) {
  void env; // Env price/download URL are no longer authorities.
  let storefront = baseStorefront();
  storefront = applyCatalogProduct(storefront, catalogProduct);
  storefront = applyReleaseEvidence(storefront, releaseEvidence);
  return storefront;
}

export const secureMacProStorefront = buildSecureMacProStorefront();

export function downloadStatusLabel(storefront) {
  if (!storefront.checkout_enabled) {
    return "Coming soon";
  }
  if (!storefront.release_evidence?.present) {
    return "Buy SecureShield Pro";
  }
  return "Get SecureShield Pro from your account";
}

/** Forbidden public claims when release evidence is absent. */
export function forbiddenUngatedClaims(storefront) {
  if (storefront.release_evidence?.present && storefront.claims?.signed_and_notarized) {
    return [];
  }
  return [
    "signed and notarized by Apple",
    "Download SecureShield Pro",
    "available now",
  ];
}
